import { MouseEvent, TouchEvent, useCallback, useEffect, useRef } from 'react';

/** Minimum press duration (ms); shorter taps keep their normal action, e.g. opening the photo */
const LONG_PRESS_MS = 400;

/** How far a finger may drift before the touch counts as a scroll rather than a press */
const MOVE_TOLERANCE_PX = 10;

/** How long after a long press ends its synthesized click may still arrive */
const CLICK_SUPPRESSION_MS = 700;

type PressEvent = TouchEvent | MouseEvent;

export interface IPoint {
    x: number;
    y: number;
}

const pointOf = (event: PressEvent): Nullable<IPoint> => {
    const point = 'touches' in event ? event.touches[0] : event;

    return point ? { x: point.clientX, y: point.clientY } : null;
};

/**
 * Calls onLongPress when the element is held for LONG_PRESS_MS without moving or scrolling.
 * Spread the returned handlers on the element whose touches should count -- one that
 * surrounding overlays (checkboxes, toolbars) bubble their touches up to.
 *
 * Releasing a long press still makes the browser synthesize a click; it's swallowed in the
 * capture phase so the press doesn't also fire the element's tap action (opening the photo,
 * toggling the lightbox chrome, playing the slideshow).
 *
 * With onDrag, moving the finger after a long press calls it instead of scrolling the page,
 * swiping the lightbox or panning a zoomed photo: until release, the drag's moves are caught on
 * the element before anything else sees them. The returned `ref` must then reach that element.
 */
const useLongPress = (
    onLongPress: (point: Nullable<IPoint>) => void,
    onDrag?: (point: IPoint) => void
) => {
    const onLongPressRef = useRef(onLongPress);
    const onDragRef = useRef(onDrag);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();
    const startRef = useRef<Nullable<IPoint>>(null);
    const didLongPressRef = useRef(false);
    const suppressClickUntilRef = useRef(0);
    const hasDrag = !!onDrag;

    onLongPressRef.current = onLongPress;
    onDragRef.current = onDrag;

    const cancelPress = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
        document.removeEventListener('scroll', cancelPress, true);
    }, []);

    useEffect(() => cancelPress, [cancelPress]);

    const detachDragRef = useRef<() => void>();

    // Listens on the pressed element itself, in the capture phase, so the drag's moves stop
    // there before the photo's own handlers (the zoom library's panning) or any ancestor's (the
    // lightbox swipe, the filmstrip) see them. It's attached for as long as dragging is possible,
    // not when the press starts: browsers decide at touchstart whether a touch's moves can be
    // cancelled, i.e. whether it may scroll.
    const dragRef = useCallback((element: Nullable<HTMLElement>) => {
        detachDragRef.current?.();
        detachDragRef.current = undefined;

        if (!element) {
            return;
        }

        const onPointerMove = (event: PointerEvent) => {
            if (didLongPressRef.current) {
                event.stopPropagation();
                onDragRef.current?.({ x: event.clientX, y: event.clientY });
            }
        };

        // A mouse also fires mousemove alongside pointermove; keep it from dragging anything
        const onMouseMove = (event: globalThis.MouseEvent) => {
            if (didLongPressRef.current) {
                event.stopPropagation();
            }
        };

        const onTouchMove = (event: globalThis.TouchEvent) => {
            if (didLongPressRef.current) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        const touchOptions = { capture: true, passive: false };

        element.addEventListener('pointermove', onPointerMove, true);
        element.addEventListener('mousemove', onMouseMove, true);
        element.addEventListener('touchmove', onTouchMove, touchOptions);

        detachDragRef.current = () => {
            element.removeEventListener('pointermove', onPointerMove, true);
            element.removeEventListener('mousemove', onMouseMove, true);
            element.removeEventListener('touchmove', onTouchMove, touchOptions);
        };
    }, []);

    const onStart = useCallback(
        (event: PressEvent) => {
            cancelPress();
            didLongPressRef.current = false;
            startRef.current = pointOf(event);
            // A scroll that starts mid-press means the user is dragging the page, even if the
            // browser stops delivering touchmove once it takes over the scroll.
            document.addEventListener('scroll', cancelPress, { capture: true, passive: true });
            timerRef.current = setTimeout(() => {
                cancelPress();
                didLongPressRef.current = true;
                onLongPressRef.current(startRef.current);
            }, LONG_PRESS_MS);
        },
        [cancelPress]
    );

    const onMove = useCallback(
        (event: PressEvent) => {
            const start = startRef.current;
            const point = pointOf(event);

            if (
                timerRef.current &&
                start &&
                point &&
                Math.hypot(point.x - start.x, point.y - start.y) > MOVE_TOLERANCE_PX
            ) {
                cancelPress();
            }
        },
        [cancelPress]
    );

    const onEnd = useCallback(() => {
        cancelPress();

        if (didLongPressRef.current) {
            didLongPressRef.current = false;
            suppressClickUntilRef.current = Date.now() + CLICK_SUPPRESSION_MS;
        }
    }, [cancelPress]);

    const onCancel = useCallback(() => {
        cancelPress();
        didLongPressRef.current = false;
    }, [cancelPress]);

    const onClickCapture = useCallback((event: MouseEvent) => {
        if (Date.now() < suppressClickUntilRef.current) {
            suppressClickUntilRef.current = 0;
            event.stopPropagation();
            event.preventDefault();
        }
    }, []);

    return {
        onTouchStart: onStart,
        onTouchMove: onMove,
        onTouchEnd: onEnd,
        onTouchCancel: onCancel,
        onMouseDown: onStart,
        onMouseMove: onMove,
        onMouseUp: onEnd,
        onMouseLeave: onCancel,
        onClickCapture,
        ref: hasDrag ? dragRef : undefined
    };
};

export default useLongPress;
